/**
 * Module dependencies.
 */

import React, { useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { TFunction } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Typography } from 'antd';
import { PhoneNumber } from 'app/components/atoms/forms/fields/phone-number/phone-number';
import { FormField } from 'app/components/atoms/forms/form-field/form-field';
import { useGetDialCodes } from 'app/hooks/app-requests/use-get-dial-codes';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useCrudForm } from 'app/components/organisms/crud/crud-form-provider';
import { parsePhoneNumber, validatePhoneNumber } from 'app/core/utils/phone-utils';

/**
 * Export `makePhoneUserLookupField`.
 */

export function makePhoneUserLookupField(type: 'add' | 'edit', translate: TFunction) {
  return function PhoneUserLookupField() {
    const form = useCrudForm();
    const { control, trigger, setError, clearErrors } = form;
    const client = useRequestProvider();

    const { data: dialCodes = [], isLoading: dialCodesLoading } = useGetDialCodes();

    const phone = useWatch({ control, name: 'phoneNumber' });
    const [debouncedPhone, setDebouncedPhone] = useState<string>(phone || '');

    useEffect(() => {
      const timer = setTimeout(() => setDebouncedPhone(phone || ''), 500);

      return () => clearTimeout(timer);
    }, [phone]);

    const parsedPhone = debouncedPhone ? parsePhoneNumber(debouncedPhone) : null;
    const dc = parsedPhone ? dialCodes.find(d => d.dialCode === parsedPhone.dialCode) : undefined;
    const isPhoneValid = parsedPhone ? validatePhoneNumber(debouncedPhone, dc?.code) : false;
    const isPhoneReady = Boolean(isPhoneValid);

    const {
      data: foundUser,
      isLoading: isLookingUp,
      isError: lookupFailed
    } = useQuery({
      queryKey: ['userByPhone', debouncedPhone],
      queryFn: () =>
        client.post(resolveAppUrl('usersByPhone'), { phoneNumber: debouncedPhone }).then((r: any) => r.data),
      enabled: isPhoneReady,
      retry: false,
      meta: { suppressError: true },
      select: data => data?.data
    });

    useEffect(() => {
      if (isLookingUp) {
        setError('phoneNumber', { message: '', type: 'lookingUp' });
      } else {
        clearErrors('phoneNumber');

        if (isPhoneReady) {
          trigger('phoneNumber');
        }
      }
    }, [isLookingUp, isPhoneReady, setError, clearErrors, trigger]);

    const isDisabled = type === 'edit' || dialCodesLoading;
    const isPhoneChanging = phone !== debouncedPhone;
    const displayName =
      !isPhoneChanging && !isLookingUp && foundUser ? foundUser.name || translate('common.labels.noName') : undefined;

    return (
      <>
        <Controller
          control={control}
          name={'phoneNumber'}
          render={({ field, fieldState: { error, invalid, isTouched } }) => (
            <PhoneNumber
              defaultDialCode={'PT'}
              dialCodes={dialCodes}
              disabled={isDisabled}
              error={error}
              invalid={invalid}
              isTouched={isTouched}
              label={String(translate('common.labels.phoneNumber'))}
              name={'phoneNumber'}
              onChange={field.onChange}
              placeholder={String(translate('common.labels.phoneNumber'))}
              value={field.value}
            />
          )}
          rules={{
            required: String(translate('common.errors.form.required')),
            validate: {
              onlyDialCode: (value: string) => {
                if (!value) return true;

                if (dialCodes.find(d => d.dialCode === value)) {
                  return translate('common.errors.form.required');
                }
              },
              validFormat: (value: string) => {
                if (!value) return true;

                const parsed = parsePhoneNumber(value);

                if (!parsed) return String(translate('common.errors.form.phoneNumber'));

                const dialCode = dialCodes.find(d => d.dialCode === parsed.dialCode);

                return (
                  validatePhoneNumber(value, dialCode?.code) || String(translate('common.errors.form.phoneNumber'))
                );
              },
              userExists: (value: string) => {
                if (!value) return true;
                if (!isLookingUp && (lookupFailed || !foundUser)) {
                  return String(translate('common.errors.form.userNotFound'));
                }

                return true;
              }
            }
          }}
        />

        <FormField label={String(translate('common.labels.user'))} style={{ marginLeft: 16 }}>
          <Typography.Text type={displayName ? undefined : 'secondary'}>{displayName ?? '—'}</Typography.Text>
        </FormField>
      </>
    );
  };
}
